export enum AUDIO_TRACK {
  BGM = 'BGM',
  COLLECT_FISH = 'COLLECT_FISH',
  DAILY_REWARD = 'DAILY_REWARD',
  LEVEL_UP = 'LEVEL_UP',
  SPIN = 'SPIN',
}

const AUDIO_PATH: Record<AUDIO_TRACK, string> = {
  [AUDIO_TRACK.BGM]: '/assets/audio/bgm.mp3',
  [AUDIO_TRACK.COLLECT_FISH]: '/assets/audio/collect-fish.mp3',
  [AUDIO_TRACK.DAILY_REWARD]: '/assets/audio/daily-reward.mp3',
  [AUDIO_TRACK.LEVEL_UP]: '/assets/audio/level-up.mp3',
  [AUDIO_TRACK.SPIN]: '/assets/audio/spin.mp3',
};

/**
 * 
 * @param {AUDIO_TRACK} track 音频轨道
 * @param {boolean} loop 是否循环播放
 * @returns
 * @memberof AudioPlayer
 * @description 支持 AudioContext 会优先使用，没有的情况下回退到 HTML5 Audio Element, 在 iOS 下面 HTML5 Audio Element 可能会碰到音频延迟的问题
 * @example
 * AudioPlayer.play(AUDIO_TRACK.BGM, true);
 * AudioPlayer.play(AUDIO_TRACK.BGM, false);
 * AudioPlayer.play(AUDIO_TRACK.BGM);
 */
class AudioPlayer {
  private audioContext: AudioContext | null = null;
  private audioBuffers: Record<AUDIO_TRACK, AudioBuffer> = {} as Record<AUDIO_TRACK, AudioBuffer>;
  private htmlAudioElements: Record<AUDIO_TRACK, HTMLAudioElement>;
  private currentSource: AudioBufferSourceNode | null = null;
  private currentHtmlAudio: HTMLAudioElement | null = null;
  private loopAudioSource: AudioBufferSourceNode | null = null;
  private loopHtmlAudio: HTMLAudioElement | null = null;

  constructor() {
    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch (e) {
      console.warn("Web Audio API is not supported, falling back to HTML5 Audio");
    }
    this.htmlAudioElements = this.createHtmlAudioElements(AUDIO_PATH);
    this.loadAudioBuffers();
  }

  private createHtmlAudioElements(paths: Record<AUDIO_TRACK, string>): Record<AUDIO_TRACK, HTMLAudioElement> {
    return Object.keys(paths).reduce((acc, key) => {
      const track = key as AUDIO_TRACK;
      acc[track] = new Audio(paths[track]);
      return acc;
    }, {} as Record<AUDIO_TRACK, HTMLAudioElement>);
  }

  private async loadAudioBuffers() {
    if (!this.audioContext) return;

    for (const [key, path] of Object.entries(AUDIO_PATH)) {
      const track = key as AUDIO_TRACK;
      const response = await fetch(path);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      this.audioBuffers[track] = audioBuffer;
    }
  }

  private playWebAudio(track: AUDIO_TRACK, loop: boolean) {
    const audioBuffer = this.audioBuffers[track];
    if (!this.audioContext || !audioBuffer) return;

    const source = this.audioContext.createBufferSource();
    source.buffer = audioBuffer;
    source.loop = loop;
    source.connect(this.audioContext.destination);

    if (loop) {
      this.stopLoop();
      this.loopAudioSource = source;
    } else {
      this.stop();
      this.currentSource = source;
    }

    source.start(0);
  }

  private playHtmlAudio(track: AUDIO_TRACK, loop: boolean) {
    const audio = this.htmlAudioElements[track];

    if (loop) {
      this.stopLoop();
      this.loopHtmlAudio = audio;
    } else {
      this.stop();
      this.currentHtmlAudio = audio;
    }

    audio.loop = loop;
    audio.play();
  }

  play(track: AUDIO_TRACK, loop: boolean = false) {
    if (this.audioContext && this.audioBuffers[track]) {
      this.playWebAudio(track, loop);
    } else {
      this.playHtmlAudio(track, loop);
    }
  }

  stop() {
    if (this.currentSource) {
      this.currentSource.stop();
      this.currentSource = null;
    }

    if (this.currentHtmlAudio) {
      this.currentHtmlAudio.pause();
      this.currentHtmlAudio.currentTime = 0;
      this.currentHtmlAudio = null;
    }
  }

  stopLoop() {
    if (this.loopAudioSource) {
      this.loopAudioSource.stop();
      this.loopAudioSource = null;
    }

    if (this.loopHtmlAudio) {
      this.loopHtmlAudio.pause();
      this.loopHtmlAudio.currentTime = 0;
      this.loopHtmlAudio = null;
    }
  }

  setLoop(loop: boolean) {
    if (this.currentSource) {
      this.currentSource.loop = loop;
    }
    if (this.currentHtmlAudio) {
      this.currentHtmlAudio.loop = loop;
    }
  }
}

const audioPlayerInstance = new AudioPlayer();
// 兼容之前的实现
export { audioPlayerInstance as AudioPlayer };